import { anagrafica_brucellosi } from './../modules/procedimenti/moduli/relazioni/brucellosi/anagrafica-brucellosi/modulo.server';
import type { ValueFormatterParams } from 'ag-grid-community'
import { Locale, addMinutes, differenceInBusinessDays, endOfWeek as endOfWeekDFNS, format, getHours, isDate, isWeekend, parse, parseISO, setHours, startOfDay, startOfWeek as startOfWeekDFNS } from 'date-fns'
import dateHolidays from 'date-holidays'
import { get, isNumber } from 'lodash'
import qs from 'qs'
import { Readable } from 'stream'
import invariant from 'tiny-invariant'
import type { ZodTypeAny } from "zod/v3"
import { z } from "zod/v3"
import { setToastError } from '~/services/toast-message.server'
import type { GetterFunction, SelectOptions, User } from '~/types'

import CodiceFiscale from 'codice-fiscale-js'
import { getTimezoneOffset, utcToZonedTime } from 'date-fns-tz'
import { itCH } from 'date-fns/locale'
import { prisma } from '~/services/db.server'
import * as XLSX from 'xlsx'
import { formatCurrency } from '~/modules/sian-crawler/tableParser';

export let hasRuolo = (user: User, ruolo: string) => {
  if (user.ruoli_user && user.ruoli_user.length > 0) {
    if (user.ruoli_user.includes(ruolo)) {
      return true
    }
  }
  return false
}

export const getFormData = async <T>(request: Request, nestedObj = false) => {
  let formQueryString = await request.text()
  // parse it into an object
  return qs.parse(formQueryString, nestedObj ? { allowDots: false, depth: 10 } : undefined) as T
}

const getFieldValue = (row: any, getter: string | Function) => {
  return typeof getter === 'string' ? get(row, getter) : getter(row)
}

export const getOptionsFromValues = <T>(values: T[] | undefined, valueField: Extract<keyof T, string> | GetterFunction<T>, label?: Extract<keyof T, string> | GetterFunction<T>): SelectOptions => {
  if (!values) return []
  invariant(Array.isArray(values), 'Values is not an array')

  return values.map(row => ({
    value: getFieldValue(row, valueField),
    label: getFieldValue(row, label || valueField),
  }))
}

export function currency(value: string | number | undefined | null, locale = 'it-IT') {
  let valueNumber = Number(value)
  if (!isNumber(valueNumber)) return null

  let formattedValue = new Intl.NumberFormat(locale, {
    style: 'currency',
    roundingMode: 'trunc',
    currency: 'EUR',
  }).format(valueNumber)

  // Se il simbolo dell'euro non è già a sinistra, lo sposta manualmente
  if (formattedValue.includes('€') && !formattedValue.startsWith('€')) {
    // Rimuove l'euro dalla posizione originale e lo sposta a sinistra
    return `€ ${formattedValue.replace('€', '').trim()}`
  }

  // Restituisce il valore formattato se l'euro è già a sinistra
  return formattedValue
}

export function currencyWithoutStyle(value: number | undefined | null, locale = 'it-IT') {
  return currency(value)?.trim().replace('€', '').trim().replace('€ ', '')
}

export const differenceInBusinessDaysIt = (dateEnd: Date, dateStart: Date) => {
  let date = new dateHolidays('IT')
  let days = differenceInBusinessDays(dateEnd, dateStart)
  let holidays = date.getHolidays()

  holidays
    .filter(holiday => holiday.type === 'public')
    .forEach(holiday => {
      if (!isWeekend(holiday.start) && holiday.start >= dateStart && holiday.start <= dateEnd) {
        days--
      }
    })

  return days
}

export const stripEmptyValues = (object: Record<string, any>) => {
  let data: Record<string, any> = {}
  for (let [key, value] of Object.entries(object)) {
    data[key] = value === '' ? null : value
  }
  return data
}

export const manageErrorWithToast = (error: any, request: any) => {
  let errorMessage = 'Errore operazione non andata a buon fine'

  if (error instanceof Error && process.env.NODE_ENV === 'development') {
    return setToastError(request, {
      title: error.message,
    })
  }
  return setToastError(request, {
    title: errorMessage,
  })
}

const timezone = 'Europe/Rome'

export function todayZoned() {
  return utcToZonedTime(new Date(), timezone)
}

export function getOffsetInHours() {
  const offset = getTimezoneOffset(timezone) / (60 * 60 * 1000)
  return offset > 1 ? 1 : offset
}

export function todayZonedWithoutHours() {
  const currentDate = new Date()
  currentDate.setHours(2, 0, 0, 0)
  return utcToZonedTime(currentDate, timezone)
}

export function today() {
  return dateTimeWithoutOffset()
}

export function dateWithoutOffset(date?: Date) {
  let _date = startOfDay(date || new Date())
  return addMinutes(_date, -_date.getTimezoneOffset())

  //return utcToZonedTime(date || new Date(), timezone)
}

export function toDayWithMinutes() {
  let data_ins = new Date()
  return addMinutes(data_ins, -data_ins.getTimezoneOffset())
}

export function formatDate(date: Date | string | undefined | null) {
  if (!date) {
    return ''
  }

  if (typeof date === 'string') {
    const dateFormat = /^\d{2}-\d{2}-\d{4}$/
    const dateFormat1 = /^\d{2}\/\d{2}\/\d{4}$/

    const isValidFormat = dateFormat.test(date)

    if (isValidFormat) {
      return date
    }
    const isValidFormat1 = dateFormat1.test(date)

    if (isValidFormat1) {
      return date
    }
  }

  let actualDate = typeof date === 'string' ? parseISO(date) : date

  return format(actualDate, 'dd/MM/yyyy', { locale: itCH })
}

/** Data già scritta in italiano (dd/MM/yyyy o dd-MM-yyyy, con ora facoltativa): non va riformattata. */
const DATA_GIA_FORMATTATA = /^\d{2}[-/]\d{2}[-/]\d{4}(\s+\d{2}:\d{2}(:\d{2})?)?$/

/**
 * Data parsata, oppure null se il valore non è una data valida.
 *
 * `parseISO` su una stringa non ISO non lancia: restituisce `Invalid Date`, ed è `format` a lanciare
 * `RangeError: Invalid time value` — dentro un loader significa 500 su tutta la pagina per un singolo
 * valore sporco. Meglio nessuna data che una pagina rotta.
 */
function toDataValida(date: Date | string): Date | null {
  let actualDate = typeof date === 'string' ? parseISO(date) : date

  return isNaN(actualDate.getTime()) ? null : actualDate
}

export function formatDateWithHours(date: Date | string | undefined | null) {

  if (!date) {
    return ''
  }

  // Alcune date arrivano GIÀ formattate in italiano (es. il metadato `data_inserimento` dei file
  // salvati dai procedimenti, scritto con questa stessa funzione): si restituiscono così come sono,
  // come fa già formatDate. Riparsarle darebbe Invalid Date.
  if (typeof date === 'string' && DATA_GIA_FORMATTATA.test(date.trim())) {
    return date.trim()
  }

  let actualDate = toDataValida(date)

  if (!actualDate) {
    return ''
  }

  return format(actualDate, 'dd/MM/yyyy HH:mm:ss', { locale: itCH })
}



export function formatDateOnlyHours(date: Date | string | undefined | null) {
  if (!date) {
    return ''
  }

  let actualDate = typeof date === 'string' ? parseISO(date) : date

  return format(addMinutes(actualDate, actualDate.getTimezoneOffset()), 'HH:mm:ss')
}

export function formatDateAndHours(date: Date | string | undefined | null) {
  if (!date) {
    return ''
  }

  let actualDate = typeof date === 'string' ? parseISO(date) : date

  return format(addMinutes(actualDate, actualDate.getTimezoneOffset()), 'dd/MM/yyyy HH:mm')
}

export function formatDateShort(date: Date | string | undefined | null) {
  if (!date) {
    return ''
  }

  let actualDate = typeof date === 'string' ? parseISO(date) : date

  let data = setHours(actualDate, getHours(actualDate) - getOffsetInHours())

  return format(data, 'dd/MM/yy')
}

export function formatDateForAPI(date: Date | string | undefined | null) {
  if (!date) {
    return ''
  }

  let actualDate = typeof date === 'string' ? parseISO(date) : date

  let data = setHours(actualDate, getHours(actualDate) - getOffsetInHours())

  return format(data, 'yyyyMMdd')
}

export function nowWithOffset() {
  let now = new Date()
  let data = setHours(now, getHours(now) + getOffsetInHours())

  return data
}

export function startNowWithOffset() {
  let now = new Date()
  now.setHours(getOffsetInHours(), 0, 0, 0)

  return now
}

export function formatDateOrarioShort(date: Date | string | undefined | null) {
  if (!date) {
    return ''
  }

  let actualDate = typeof date === 'string' ? parseISO(date) : date

  return format(addMinutes(actualDate, actualDate.getTimezoneOffset()), 'HH:mm')
}

export function findIntoJson(array: any[], string: string) {
  return array.reduce((r, o) => {
    if (Object.values(o).some(v => v === string)) {
      r.push(o)
      return r
    }
    if (Array.isArray(o.subNames)) {
      var subNames = findIntoJson(o.subNames, string)
      if (subNames.length) r.push(Object.assign({}, o, { subNames }))
    }
    return r
  }, [])
}

export function searchKeyOnJson(obj: any, key: string): any {
  for (const k in obj) {
    if (k === key) {
      return obj[k]
    }
    if (typeof obj[k] === 'object') {
      const result = searchKeyOnJson(obj[k], key)
      if (result) {
        return result
      }
    }
  }
  return undefined
}

export const boolean = (schema?: ZodTypeAny) => z.preprocess(value => (value ? true : false), schema || z.boolean())

export const booleanSelect = (schema?: ZodTypeAny) => z.preprocess(value => (value === 'true' ? true : value === 'false' ? false : undefined), schema || z.boolean())

// Funzione Zod per il controllo della data
export const dateNotFutureZod = (schema?: ZodTypeAny) =>
  z
    .preprocess(value => {
      if (!value) return undefined
      const parsedDate = dateWithoutOffset(parse((value as string).substring(0, 10), 'yyyy-MM-dd', new Date()))
      return parsedDate
    }, schema || z.date())
    .refine(
      date => {
        const today = dateWithoutOffset(new Date()) // Data odierna senza l'offset
        return date <= today // Controlla che la data non sia futura
      },
      {
        message: 'La data non può essere nel futuro',
      },
    )

export const trueZod = z.literal('true', { errorMap: () => ({ message: 'Obbligatorio' }) })
export const falseZod = z.literal(false, { errorMap: () => ({ message: 'Obbligatorio' }) })
export const checkBoxZod = z
  .union([trueZod, falseZod])
  .transform(value => (!value ? false : value))
  .default(false)

export const dateZod = (schema?: ZodTypeAny) =>
  z.preprocess(value => {
    return value ? dateWithoutOffset(parse((value as string).substring(0, 10), 'yyyy-MM-dd', new Date())) : undefined
  }, schema || z.date())

export const dateZod2 = (schema?: ZodTypeAny) =>
  z.preprocess(value => {
    return value && !(value instanceof Date) ? dateWithoutOffset(parse((value as string).substring(0, 10), 'yyyy-MM-dd', new Date())) : undefined
  }, schema || z.date())

export const dateZod3 = (schema?: ZodTypeAny) =>
  z.preprocess(value => {
    return value && !(value instanceof Date) ? dateWithoutOffset(parse((value as string).substring(0, 10), 'yyyy-MM-dd', new Date())) : value
  }, schema || z.date())

export const optionalDateZod3 = (schema?: ZodTypeAny) =>
  z.preprocess(value => {
    if (value == null || value === '') {
      return undefined // Rendi opzionale
    }
    return value // Passa tutto il resto a dateZod3
  }, dateZod3(schema).optional())

export const numericString = (schema?: ZodTypeAny) =>
  z.preprocess(num => {
    if (typeof num === 'string' && num !== '') {
      return parseFloat(num)
    } else if (typeof num === 'number') {
      return num
    } else {
      return undefined
    }
  }, schema || z.number())

export const numericStringOptional = (schema?: ZodTypeAny) =>
  z.preprocess(num => {
    if (typeof num === 'string' && num !== '') {
      return parseFloat(num)
    } else if (typeof num === 'number') {
      return num
    } else {
      return null
    }
  }, schema || z.number().nullish())

export const stringOptional = (schema?: ZodTypeAny) => z.preprocess(str => (str === '' ? undefined : str), schema || z.string().nullish())

export function currencyFormatter(params: ValueFormatterParams) {
  if (isNaN(params.value)) {
    return ''
  }
  return currency(Number(params.value)) ?? ''
}

export function dataFormatterWithHours(params: ValueFormatterParams) {
  return formatDateWithHours(params.value) ?? ''
}

export function dataFormatterOnlyHours(params: ValueFormatterParams) {
  return formatDateOnlyHours(params.value) ?? ''
}

export function dataFormatterWithoutHours(params: ValueFormatterParams) {
  return formatDateShort(params.value) ?? ''
}

export function dataFormatter(params: ValueFormatterParams) {
  return formatDate(params.value) ?? ''
}

export function formatDateInObject(object: any): any {
  for (let obj in object) {
    let flag = false
    if (typeof object[obj] === 'object' && (obj.includes('date') || obj.includes('data') || obj.includes('ated_at'))) {
      object[obj] = formatDate(object[obj])
      flag = true
    } else if (typeof object[obj] === 'string' && (obj.includes('date') || obj.includes('data') || (obj.includes('ated_at') && isDate(object[obj])))) {
      object[obj] = formatDate(object[obj])
      flag = true
    }

    if (object[obj] != null && typeof object[obj] === 'object' && (obj.includes('punteggio') || obj.includes('importo') || obj.includes('spesa') || obj.includes('contributo'))) {
      object[obj] = Number(object[obj])
    }

    if (object[obj] && typeof object[obj] === 'object' && !flag) {
      formatDateInObject(object[obj])
    }
  }

  return object
}

export function Uint8ArrayToStream(buffer: Uint8Array) {
  let stream = new Readable()
  stream.push(buffer)
  stream.push(null)
  return stream
}

export async function promiseStringToStream(promiseString: Promise<string>) {
  const data = await Promise.resolve(promiseString)
  const stream = new Readable({
    read() {
      this.push(data)
      this.push(null)
    },
  })
  return stream
}

export function responseBodyToAsyncIterable(response: Response) {
  const reader = response.body?.getReader()
  if (!reader) {
    throw new Error('Response body is not readable')
  }
  return {
    [Symbol.asyncIterator]() {
      return {
        async next() {
          const { done, value } = await reader.read()
          return { done, value }
        },
      }
    },
  }
}

// function to get filename from request header content-disposition
export function getFilenameFromDisposition(disposition: string | null) {
  if (disposition && disposition.indexOf('attachment') !== -1) {
    let filenameRegex = /filename[^;=\n]*=((['"]).*?\2|[^;\n]*)/
    let matches = filenameRegex.exec(disposition)
    if (matches != null && matches[1]) {
      return matches[1].replace(/['"]/g, '')
    }
  }
  return undefined
}

export async function downloadFromResponse(res: Response, filename?: string) {
  if (res.status !== 200) return null

  let link = document.createElement('a')

  let filenameFromDisposition = getFilenameFromDisposition(res.headers.get('content-disposition'))

  link.download = filename ?? filenameFromDisposition ?? 'file'

  let blob = await res.blob()
  let url = URL.createObjectURL(blob)
  link.href = url

  document.body.appendChild(link)
  link.click()
  link.remove()

  // La revoke DEVE essere posticipata: revocare l'object URL in modo sincrono subito
  // dopo il click fa annullare il download al browser (il blob viene invalidato
  // prima che il download inizi davvero)
  setTimeout(() => URL.revokeObjectURL(url), 30_000)

  return blob
}

export async function downloadFromBinaryData(data: Uint8Array, filename?: string) {
  let link = document.createElement('a')

  link.download = filename ?? 'file'

  let blob = new Blob([data])
  let url = URL.createObjectURL(blob)
  link.href = url

  document.body.appendChild(link)
  link.click()
  link.remove()

  // Revoke posticipata: vedi downloadFromResponse
  setTimeout(() => URL.revokeObjectURL(url), 30_000)

  return blob
}

export async function getBufferFromResponse(response: Response): Promise<Buffer> {
  const arrayBuffer = await response.arrayBuffer()
  return Buffer.from(arrayBuffer)
}

export async function asyncSome<T>(array: T[], callback: ((element: T, index: number, array: T[]) => Promise<boolean | undefined>) | undefined): Promise<boolean> {
  for (let i = 0; i < array.length; i++) {
    if (callback && (await callback(array[i], i, array))) {
      return true
    }
  }
  return false
}

export async function asyncEvery<T>(array: T[], callback: ((element: T, index: number, array: T[]) => Promise<boolean | undefined>) | undefined): Promise<boolean> {
  for (let i = 0; i < array.length; i++) {
    if (callback && !(await callback(array[i], i, array))) {
      return false
    }
  }
  return true
}

export function startOfWeek(date?: Date | string | null) {
  return startOfWeekDFNS(getDateTime(date), { weekStartsOn: 1 })
}

export function endOfWeek(date?: Date | string | null) {
  return endOfWeekDFNS(getDateTime(date), { weekStartsOn: 1 })
}

export function dateTimeWithoutOffset(date?: Date | string | null) {
  let _date = getDateTime(date)
  return addMinutes(_date, -_date.getTimezoneOffset())

  //return utcToZonedTime(getDateTime(date), timezone)
}

export function getDateTime(date?: Date | string | null) {
  return typeof date === 'string' ? parseISO(date) : date ?? new Date()
}

export function getDate(date?: Date | string | null) {
  return dateTimeWithoutOffset(startOfDay(getDateTime(date)))
}

export function parseDateForModuli(dateString: string) {
  // Parse della data nel formato corretto
  const parsedDate = parse(dateString, 'dd-MM-yyyy', new Date())

  // Formattazione della data in 'yyyy-MM-dd'
  return format(parsedDate, 'yyyy-MM-dd')
}

const isValidPartitaIva = (piva: string) => {
  if (!/^\d{11}$/.test(piva)) {
    return false // Deve essere composta da 11 numeri
  }

  let sum = 0

  // Somma le cifre in posizione dispari
  for (let i = 0; i <= 9; i += 2) {
    sum += parseInt(piva[i], 10)
  }

  // Somma le cifre in posizione pari con l'algoritmo di moltiplicazione
  for (let i = 1; i <= 9; i += 2) {
    let temp = 2 * parseInt(piva[i], 10)
    if (temp > 9) {
      temp = temp - 9
    }
    sum += temp
  }

  // Calcolo del checksum
  const checkDigit = (10 - (sum % 10)) % 10

  return checkDigit === parseInt(piva[10], 10)
}

const isValidPartitaIvaSimpleCheck = (piva: string) => {
  if (!/^\d{11}$/.test(piva)) {
    return false // Deve essere composta da 11 numeri
  }

  return true
}

// Tabelle per calcolo del carattere di controllo
const oddChars = {
  0: 1,
  1: 0,
  2: 5,
  3: 7,
  4: 9,
  5: 13,
  6: 15,
  7: 17,
  8: 19,
  9: 21,
  A: 1,
  B: 0,
  C: 5,
  D: 7,
  E: 9,
  F: 13,
  G: 15,
  H: 17,
  I: 19,
  J: 21,
  K: 1,
  L: 0,
  M: 5,
  N: 7,
  O: 9,
  P: 13,
  Q: 15,
  R: 17,
  S: 19,
  T: 21,
  U: 1,
  V: 0,
  W: 5,
  X: 7,
  Y: 9,
  Z: 21,
}

const evenChars = {
  0: 0,
  1: 1,
  2: 2,
  3: 3,
  4: 4,
  5: 5,
  6: 6,
  7: 7,
  8: 8,
  9: 9,
  A: 0,
  B: 1,
  C: 2,
  D: 3,
  E: 4,
  F: 5,
  G: 6,
  H: 7,
  I: 8,
  J: 9,
  K: 10,
  L: 11,
  M: 12,
  N: 13,
  O: 14,
  P: 15,
  Q: 16,
  R: 17,
  S: 18,
  T: 19,
  U: 20,
  V: 21,
  W: 22,
  X: 23,
  Y: 24,
  Z: 25,
}

// Funzione per calcolare il carattere di controllo
const calculateControlCharacter = (codiceFiscale: string) => {
  let sum = 0

  // Somma i valori delle posizioni dispari (indice pari in JavaScript)
  for (let i = 0; i < 15; i++) {
    const char = codiceFiscale[i]
    if (i % 2 === 0) {
      sum += oddChars[char.toUpperCase() as keyof typeof oddChars]
    } else {
      sum += evenChars[char.toUpperCase() as keyof typeof evenChars]
    }
  }

  // Calcola il carattere di controllo basato sul modulo 26
  const remainder = sum % 26
  return String.fromCharCode(remainder + 65) // 65 è il codice ASCII per 'A'
}

// Funzione per validare il Codice Fiscale
const isValidCodiceFiscale = (codiceFiscale: string) => {
  if (!/^[A-Z0-9]{16}$/.test(codiceFiscale)) {
    return false // Controlla che abbia 16 caratteri alfanumerici
  }

  const controlChar = calculateControlCharacter(codiceFiscale)
  return controlChar === codiceFiscale[15].toUpperCase()
}

// Definizione schema zod per il Codice Fiscale

// Definizione schema zod per il Codice Fiscale
export const codiceFiscaleSchema = z.string().refine(isValidCodiceFiscale, {
  message: 'Codice Fiscale non valido',
})

// Definizione schema zod
export const partitaIvaSchema = z.string().refine(isValidPartitaIva, {
  message: 'Partita IVA non valida',
})

export const partitaIvaSimpleSchema = z.string().refine(isValidPartitaIvaSimpleCheck, {
  message: 'Partita IVA non valida',
})

export function ricavaGenereDaCodiceFiscale(codiceFiscale: string) {
  if (codiceFiscale.length !== 16) {
    return 'NT'
  }

  // Il giorno di nascita è contenuto nei caratteri dalla posizione 9 alla 10
  const giornoNascita = parseInt(codiceFiscale.substr(9, 2), 10)

  // Se il giorno è maggiore di 31, è una donna (40 è stato aggiunto)
  return giornoNascita > 31 ? 'F' : 'M'
}

export function supCondottaToMetriQuadri(supCondotta: string) {
  // Dividi la stringa in componenti separati (ettari, are, centiare)
  const [ettari, are, centiare] = supCondotta.split('.').map(Number)

  // 1 ettaro = 10.000 metri quadrati
  const metriQuadriDaEttari = ettari * 10000

  // 1 ara = 100 metri quadrati
  const metriQuadriDaAre = are * 100

  // 1 centiara = 1 metro quadrato
  const metriQuadriDaCentiare = centiare

  // Somma tutti i metri quadrati ottenuti
  return metriQuadriDaEttari + metriQuadriDaAre + metriQuadriDaCentiare
}

export function cfValidate(codice_fiscale: string) {
  try {
    new CodiceFiscale(codice_fiscale)
    return true
  } catch (e) {
    return false
  }
}

export async function sleep(ms: number) {
  return new Promise(resolve => setTimeout(resolve, ms))
}

export async function domandeBrucellosiAmmissibili() {
  const cuaaEsclusi = ['GRFGNR75A26M260W', 'DDMSVT94A15B715R', 'MRTGPP84R28B963E', 'PCCFNC71D08I247L']

  const procedimenti = await prisma.procedimento_compilazione.findMany({
    where: { tipo: 'istruttoria_brucellosi' },
    select: { id: true, content: true, context: true },
  })

  if (!procedimenti.length) return []

  const procedimentiFiltrati = procedimenti.filter(p => {
    const cuaa = (p.content as any)?.anagrafica_istruttoria?.cuaa
    return !cuaa || !cuaaEsclusi.includes(cuaa)
  })

  return procedimentiFiltrati
}

